/**
 * Contact page — small form that POSTs to /api/contact.
 *
 * The backend logs each submission for the operator; outbound email is
 * gated behind SendGrid configuration. In all envs we acknowledge with
 * a 202 once the payload validates so the user gets immediate feedback.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/hooks/useAuth';

const API = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL ?? '/api';

export function ContactPage() {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Prefill from the logged-in user when available.
  useEffect(() => {
    if (user) {
      if (!name) setName(user.displayName);
      if (!email && user.email) setEmail(user.email);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setStatus('ok');
      setSubject('');
      setMessage('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <article className="container mx-auto px-4 py-16 max-w-2xl">
      <h1 className="text-4xl font-display font-bold text-white mb-3">Contact</h1>
      <p className="text-white/60 mb-8">
        Reach the Re:Nav-96 admins about membership, photos, the upcoming reunion, or anything else.
        Privacy questions covered in our{' '}
        <Link to="/privacy" className="link">Privacy Policy</Link>.
      </p>

      {status === 'ok' ? (
        <div className="card text-center py-10">
          <h2 className="text-2xl font-display font-bold text-white mb-2">Thanks — message received.</h2>
          <p className="text-white/60">
            An admin will follow up via email. You can submit another at any time.
          </p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="btn-secondary mt-6"
          >
            Send another
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="card space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/80 mb-1">Your name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Ravi K"
              />
            </div>
            <div>
              <label className="block text-sm text-white/80 mb-1">Your email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Subject</label>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              className="input"
              placeholder="Reunion logistics"
            />
          </div>
          <div>
            <label className="block text-sm text-white/80 mb-1">Message</label>
            <textarea
              required
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              className="input"
              placeholder="What's on your mind?"
            />
            <p className="text-xs text-white/40 mt-1">{message.length} / 5000</p>
          </div>
          {status === 'error' && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded">
              <p className="text-red-400 text-sm">{errorMsg ?? 'Failed to send'}</p>
            </div>
          )}
          <button type="submit" disabled={submitting} className="btn-primary">
            {submitting ? 'Sending…' : 'Send message'}
          </button>
        </form>
      )}
    </article>
  );
}

export default ContactPage;
